import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import orderAPI from '../../api/orderAPI';
import { formatDate, parseDate } from '../../utils/dateUtils';
import ReturnModal from '../../components/ReturnModal';
import CancelModal from '../../components/CancelModal'; 
import DateEditor from '../../components/DateEditor'; 
import { formatNumber} from '../../utils/numberUtils';

const SalesOrderDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isEditingDate, setIsEditingDate] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [returnOrders, setReturnOrders] = useState([]);

    // Print styles
    const printStyles = `
        @media print {
            .sidebar, .no-print {
                display: none !important;
            }
            .main-content {
                margin-left: 0 !important;
                width: 100% !important;
            }
            body {
                background: white !important;
            }
            .print-area {
                padding: 0 !important;
                margin: 0 !important;
            }
            .date-edit-btn {
                display: none !important;
            }
        }
    `;

    useEffect(() => {
        fetchOrderData();
        
        // Inject print styles
        const styleElement = document.createElement('style');
        styleElement.innerHTML = printStyles;
        document.head.appendChild(styleElement);
        
        return () => {
            document.head.removeChild(styleElement);
        };
    }, [id]);

    // Fetch return orders when component loads
    useEffect(() => {
        if (order && (order.status === 'returned' || order.status === 'partially_returned')) {
        orderAPI.getReturnOrders(id)
            .then(response => setReturnOrders(response.data))
            .catch(err => console.error('Failed to fetch return orders:', err));
        }
    }, [order, id]);

    const fetchOrderData = async () => {
        try { 
            setLoading(true);
            setError(null);
            const orderResponse = await orderAPI.getById(id);
            setOrder(orderResponse.data);
        } catch (err) {
            setError(err.message || 'Failed to load sales order');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateOrderDate = async (newDate) => {
        try {
            setIsSaving(true);
            await orderAPI.updateOrderDate(id, { date: newDate });
            
            // Update local state
            setOrder(prev => ({
                ...prev,
                createdAt: newDate
            }));
            
            setIsEditingDate(false);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to update order date');
        } finally {
            setIsSaving(false);
        }
    };

     // Updated Print Function - Similar to PO approach
     const handlePrintReceipt = () => {
        if (!order) return;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Sales Receipt - ${order.orderNumber}</title>
                    <style>
                        body { 
                            font-family: 'Courier New', monospace; 
                            margin: 0; 
                            padding: 10px; 
                            font-size: 12px;
                            line-height: 1.2;
                        }
                        .receipt-header { 
                            text-align: center; 
                            margin-bottom: 15px;
                            border-bottom: 1px dashed #000;
                            padding-bottom: 10px;
                        }
                        .receipt-header h1 { 
                            margin: 0; 
                            font-size: 16px;
                            font-weight: bold;
                        }
                        .receipt-header h2 {
                            margin: 5px 0;
                            font-size: 14px;
                        }
                        .store-info {
                            text-align: center;
                            margin-bottom: 10px;
                        }
                        .customer-info {
                            margin-bottom: 10px;
                            padding-bottom: 5px;
                            border-bottom: 1px dashed #000;
                        }
                        table { 
                            width: 100%; 
                            border-collapse: collapse; 
                            margin: 10px 0;
                        }
                        th, td { 
                            padding: 4px 2px; 
                            text-align: left;
                            border-bottom: 1px dashed #ddd;
                        }
                        th { 
                            border-bottom: 1px solid #000;
                            font-weight: bold;
                        }
                        .text-right { text-align: right; }
                        .text-center { text-align: center; }
                        .text-left { text-align: left; }
                        .border-top {
                            border-top: 1px solid #000;
                            padding-top: 5px;
                            margin-top: 5px;
                        }
                        .summary-section {
                            margin-top: 15px;
                            border-top: 2px solid #000;
                            padding-top: 10px;
                        }
                        .summary-row {
                            display: flex;
                            justify-content: space-between;
                            margin: 3px 0;
                        }
                        .thank-you {
                            text-align: center;
                            margin-top: 20px;
                            font-weight: bold;
                            border-top: 1px dashed #000;
                            padding-top: 10px;
                        }
                        .payment-info {
                            margin: 10px 0;
                            padding: 5px;
                            border: 1px dashed #000;
                        }
                        @media print {
                            body { margin: 0; padding: 10px; }
                        }
                    </style>
                </head>
                <body>
                    <div class="receipt-header">
                        <h1>Retail Client</h1>
                        <h2>SALES RECEIPT</h2>
                        <div class="store-info">
                            <div>Order #: ${order.orderNumber}</div>
                            <div>Date: ${formatDate(order.createdAt, 'short')}</div>
                            <div>Time: ${formatDate(order.createdAt, 'time')}</div>
                        </div>
                    </div>

                    <div class="customer-info">
                        <div><strong>Customer:</strong> ${order.customer?.name || 'Walk-in Customer'}</div>
                        ${order.customer?.phone ? `<div><strong>Phone:</strong> ${order.customer.phone}</div>` : ''}
                        ${order.customer?.accountNumber ? `<div><strong>Account:</strong> ${order.customer.accountNumber}</div>` : ''}
                        <div><strong>Cashier:</strong> ${order.cashier?.name || 'System'}</div>
                        ${order.salesman ? `<div><strong>Salesman:</strong> ${order.salesman}</div>` : ''}
                    </div>

                    <table>
                        <thead>
                            <tr>
                                <th class="text-left">Item</th>
                                <th class="text-center">Qty</th>
                                <th class="text-right">Price</th>
                                <th class="text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${order.items && order.items.map(item => `
                                <tr>
                                    <td class="text-left">
                                        ${item.product?.name || 'Unknown Product'}
                                        ${item.product?.sku ? `<br><small>SKU: ${item.product.sku}</small>` : ''}
                                    </td>
                                    <td class="text-center">${item.quantity}</td>
                                    <td class="text-right">${formatNumber(item.priceAtSale || 0)}</td>
                                    <td class="text-right">${formatNumber(item.quantity * item.priceAtSale)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>

                    <div class="summary-section">
                        <div class="summary-row">
                            <span>Subtotal:</span>
                            <span>${formatNumber(order.subtotal || 0)}</span>
                        </div>
                        ${order.discountTotal > 0 ? `
                            <div class="summary-row">
                                <span>Discount:</span>
                                <span>-${formatNumber(order.discountTotal || 0)}</span>
                            </div>
                        ` : ''}
                        ${order.tax > 0 ? `
                            <div class="summary-row">
                                <span>Tax:</span>
                                <span>${formatNumber(order.tax || 0)}</span>
                            </div>
                        ` : ''}
                        <div class="summary-row border-top">
                            <strong>Total:</strong>
                            <strong>${formatNumber(order.total || 0)}</strong>
                        </div>
                    </div>

                    ${order.payment ? `
                        <div class="payment-info">
                            <div class="summary-row">
                                <span>Payment Method:</span>
                                <span>${order.payment.method?.toUpperCase() || 'CASH'}</span>
                            </div>
                            ${order.payment.tendered ? `
                                <div class="summary-row">
                                    <span>Amount Tendered:</span>
                                    <span>${formatNumber(order.payment.tendered || 0)}</span>
                                </div>
                            ` : ''}
                            ${order.payment.changeDue ? `
                                <div class="summary-row">
                                    <span>Change Due:</span>
                                    <span>${formatNumber(order.payment.changeDue || 0)}</span>
                                </div>
                            ` : ''}
                        </div>
                    ` : ''}

                    <div class="thank-you">
                        THANK YOU FOR YOUR BUSINESS!<br>
                        PLEASE COME AGAIN
                    </div>

                    <div style="text-align: center; margin-top: 10px; font-size: 10px;">
                        ${formatDate(order.createdAt, 'long')}
                    </div>

                    <script>
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                                setTimeout(function() {
                                    window.close();
                                }, 100);
                            }, 250);
                        }
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleVoidOrder = async () => {
        if (!window.confirm('Are you sure you want to void this order? This action cannot be undone.')) {
            return;
        }
        
        try {
            setLoading(true);
            await orderAPI.voidOrder(id);
            fetchOrderData();
            setError(null);
            alert('Order voided successfully');
        } catch (err) {
            setError(err.message || 'Failed to void order');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelOrder = () => {
        setShowCancelModal(true);
    };

    const handleProcessCancel = async (cancelData) => {
        try {
            setLoading(true);
            const result = await orderAPI.cancel(id, cancelData);
            fetchOrderData();
            setError(null);
            setShowCancelModal(false);
            alert(result.message || 'Order cancelled successfully');
        } catch (err) {
            setError(err.message || 'Failed to cancel order');
        } finally {
            setLoading(false);
        }
    };


    const calculateItemTotal = (item) => {
        const quantity = item.quantity || 0;
        const price = item.priceAtSale || 0;
        return (quantity * price);
    };

    const handleReturnOrder = () => {
        setShowReturnModal(true);
        setSelectedItems(order.items.map(item => ({
            itemId: item._id,
            quantity: 0,
            maxQuantity: item.quantity,
            productName: item.product?.name || 'Unknown Product'
        })));
    };
    
    const handleProcessReturn = async (returnData) => {
        if (!window.confirm('Are you sure you want to process this return?')) {
          return;
        }
      
        try {
          setLoading(true);
          const result = await orderAPI.returnOrder(id, returnData);
          
          // Refresh data
          await fetchOrderData();
          
          // Fetch return orders
          const returnsResponse = await orderAPI.getReturnOrders(id);
          setReturnOrders(returnsResponse.data);
          
          setError(null);
          setShowReturnModal(false);
          alert(result.message || 'Return processed successfully');
        } catch (err) {
          setError(err.message || 'Failed to process return');
        } finally {
          setLoading(false);
        }
      };
    // Add to SalesOrderDetailPage.jsx
    const handleCreateInvoice = async () => {
        try {
        setLoading(true);
        const response = await orderAPI.createInvoice(id);
        
        // Update local state
        setOrder(prev => ({
            ...prev,
            invoice: response.data.invoice._id,
            invoiceNumber: response.data.invoice.invoiceNumber
        }));
        
        setError(null);
        alert('Invoice created successfully');
        } catch (err) {
        setError(err.message || 'Failed to create invoice');
        } finally {
        setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'completed': { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
            'Cancelled': { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
            'Voided': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Voided' },
            'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
            'refunded': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Refunded' }
        };
        
        const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
        return (
            <span className={`ml-2 px-3 py-1 rounded-full text-sm font-semibold ${config.bg} ${config.text}`}>
                {config.label}
            </span>
        );
    };

    if (loading) {
        return (
            <Layout pageTitle="Loading...">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
                </div>
            </Layout>
        );
    }

    if (!order) {
        return (
            <Layout pageTitle="Not Found">
                <div className="bg-white rounded-lg shadow-md p-6 text-center">
                    <h2 className="text-xl font-semibold mb-4">Sales Order Not Found</h2>
                    <button 
                        onClick={() => navigate('/sales-order')}
                        className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 transition-colors"
                    >
                        Back to List
                    </button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle={`Sales Order #${order.orderNumber}`}>
            <div className="max-w-6xl mx-auto p-6 bg-gray-50">
                {/* Header Section */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Sales Order Details</h1>
                        <p className="text-gray-600 mt-1">Order #{order.orderNumber}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button 
                            onClick={handlePrintReceipt}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50 no-print"
                            disabled={loading}
                        >
                            {loading ? 'Printing...' : '📄 Print Receipt'}
                        </button>
                        <button 
                            onClick={() => navigate('/sales-order')}
                            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 transition-colors no-print"
                        >
                            ← Back to List
                        </button>
                    </div>
                </div>

                {/* Error Alert */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex justify-between items-center">
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                            ×
                        </button>
                    </div>
                )}

                {/* Order Header Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Order Information */}
                        <div className="space-y-4">
                            <h3 className="font-semibold text-gray-900 text-lg border-b pb-2">Order Information</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="font-medium text-gray-700">Order Number:</span>
                                    <span className="text-gray-900">{order.orderNumber}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="font-medium text-gray-700">Status:</span>
                                    {getStatusBadge(order.status)}
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="font-medium text-gray-700">Date:</span>
                                    <div className="flex items-center gap-2">
                                        {isEditingDate ? (
                                            <DateEditor
                                                date={order.createdAt}
                                                onSave={handleUpdateOrderDate}
                                                onCancel={() => setIsEditingDate(false)}
                                                loading={isSaving}
                                            />
                                        ) : (
                                            <>
                                                <span className="text-gray-900">{formatDate(order.createdAt)}</span>
                                                <button
                                                    onClick={() => setIsEditingDate(true)}
                                                    className="text-blue-600 hover:text-blue-800 text-sm date-edit-btn no-print"
                                                    title="Edit date"
                                                >
                                                    ✏️
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {order.invoice && (
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Invoice:</span>
                                <div className="flex items-center gap-2">
                                <span className="text-gray-900">{order.invoiceNumber}</span>
                                <button
                                    onClick={() => navigate(`/invoice/${order.invoice}`)}
                                    className="text-blue-600 hover:text-blue-800 text-sm no-print"
                                    title="View Invoice"
                                >
                                    📄
                                </button>
                                </div>
                            </div>
                            )}

                            {order.payment?.method === 'charge' && !order.invoice && order.status === 'completed' && (
                            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-sm text-yellow-800 font-medium">
                                    Charge order - Invoice not created
                                    </p>
                                    <p className="text-xs text-yellow-600">
                                    Create invoice to post to accounting
                                    </p>
                                </div>
                                <button
                                    onClick={handleCreateInvoice}
                                    className="px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600"
                                    disabled={loading}
                                >
                                    {loading ? 'Creating...' : 'Create Invoice'}
                                </button>
                                </div>
                            </div>
                            )}
                        </div>

                        {/* Customer Information */}
                        <div className="space-y-4">
                            <h3 className="font-semibold text-gray-900 text-lg border-b pb-2">Customer</h3>
                            <div className="space-y-3">
                                <div>
                                    <p className="font-semibold text-gray-900 text-lg">
                                        {order.customer?.name || 'Walk-in Customer'}
                                    </p>
                                    {order.customer?.phone && (
                                        <p className="text-gray-600 flex items-center gap-2 mt-1">
                                            📞 {order.customer.phone}
                                        </p>
                                    )}
                                    {order.customer?.email && (
                                        <p className="text-gray-600 flex items-center gap-2 mt-1">
                                            ✉️ {order.customer.email}
                                        </p>
                                    )}
                                </div>
                                {order.customer?.taxExempt && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        Tax Exempt
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Store & Payment Information */}
                        <div className="space-y-4">
                            <h3 className="font-semibold text-gray-900 text-lg border-b pb-2">Store & Payment</h3>
                            <div className="space-y-3">
                                <div>
                                    <p className="font-semibold text-gray-900">{order.store?.name || 'N/A'}</p>
                                    <p className="text-gray-600 text-sm mt-1">Cashier: {order.cashier?.name || 'System'}</p>
                                    {order.salesRep && (
                                        <p className="text-gray-600 text-sm">Sales Rep: {order.salesRep.name}</p>
                                    )}
                                </div>
                                <div className="pt-2 border-t border-gray-100">
                                    <p className="text-gray-700">
                                        <span className="font-medium">Payment Method:</span>{' '}
                                        <span className="capitalize">{order.payment?.method || 'N/A'}</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Order Items Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-gray-900">Order Items</h2>
                        <span className="text-gray-500">{order.items?.length || 0} items</span>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {order.items && order.items.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="font-medium text-gray-900">
                                                {item.product?.name || 'Unknown Product'}
                                            </div>
                                            {item.product?.sku && (
                                                <div className="text-sm text-gray-500">SKU: {item.product.sku}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                                            {item.quantity}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                                            {formatNumber(item.priceAtSale || 0)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                                            {formatNumber(item.discount || 0)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">
                                            {formatNumber(calculateItemTotal(item))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Order Summary */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex justify-end">
                        <div className="w-full md:w-1/3 space-y-3">
                            <div className="flex justify-between text-lg">
                                <span className="font-semibold text-gray-700">Total:</span>
                                <span className="font-bold text-gray-900">{formatNumber(order.total || 0)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Return Orders Section */}
                {returnOrders.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Return Orders</h2>
                    <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Return #</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Refund</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {returnOrders.map((returnOrder) => (
                            <tr key={returnOrder._id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="font-medium text-blue-600">{returnOrder.orderNumber}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(returnOrder.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatNumber(returnOrder.total || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {returnOrder.returnReason || 'No reason provided'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                onClick={() => navigate(`/sales-order/${returnOrder._id}`)}
                                className="text-blue-600 hover:text-blue-900"
                                >
                                View Details
                                </button>
                            </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                </div>
                )}

                {/* Action Buttons */}
                {order.status === 'completed' && (
                    <div className="flex flex-wrap justify-end gap-3 no-print">
                        <button
                            onClick={handleReturnOrder}
                            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 font-medium"
                            disabled={loading}
                        >
                            Return Order
                        </button>
                        <button
                            onClick={handleCancelOrder}
                            className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 font-medium"
                            disabled={loading}
                        >
                            Cancel Order
                        </button>
                    </div>
                )}

            </div>


            {/* Return Modal */}
            {showReturnModal && (
                <ReturnModal
                    items={selectedItems}
                    onClose={() => setShowReturnModal(false)}
                    onConfirm={handleProcessReturn}
                    loading={loading}
                />
            )}

             {/* Cancel Modal */}
             {showCancelModal && (
                <CancelModal
                    order={order}
                    onClose={() => setShowCancelModal(false)}
                    onConfirm={handleProcessCancel}
                    loading={loading}
                />
            )}
        </Layout>
    );
};

export default SalesOrderDetailPage;